import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditorInput from 'components/inputs/EditorInput';
import Button from 'components/buttons/Button';
import { ContentEditorWrapper, TextPlain } from './Styled';
import ContentValidator from './ContentValidator';
import TwitterText from 'twitter-text';
import { usePostActions } from '../PostActions';
import { useParams } from 'react-router-dom';
import { CampaignParams } from 'pages/CampaignDetailPage/CampaignDetailPage';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { ErrorSpan } from 'components/inputs/UploadFile/StyleUploadFile';
import DateTimePicker from 'react-datetime-picker';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import { ICampaigns } from 'redux/slices/Campaigns/Campaigns.interface';
import Tabs from 'components/Tabs';
import { AcceptPostRequest } from 'services/rest/post';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

export interface ContentEditorProps {
  provider: string;
  postId: number;
  label: string;
  maxCharacters?: number;
  originalContent: string;
  originalContent2?: string;
  originalContent3?: string;
  shouldValidateContent?: boolean;
  setShowButton?: any;
  getRankingData?: any;
  setPublicationLoader?: any;
  isTwitter: boolean;
  disabled: boolean;
  campaign?: ICampaigns;
  programmerPost: boolean;
  contents: any;
}

const ContentEditor: React.FC<ContentEditorProps> = props => {
  const [valueDate, setValueDate] = useState<Value>(new Date());
  const { campaignId } = useParams<CampaignParams>();
  const [contentTab, setContentTab] = useState(0);
  const [instagramOptions, setInstagramOptions] = useState({
    shareInFeed: true,
    shareInStories: false
  });

  const {
    provider,
    postId,
    label,
    maxCharacters,
    originalContent,
    originalContent2,
    originalContent3,
    shouldValidateContent = true,
    setShowButton,
    getRankingData,
    setPublicationLoader,
    isTwitter,
    disabled,
    campaign,
    programmerPost,
    contents
  } = props;
  const isManualShareCampaign =
    ['instagram', 'tiktok'].includes(provider) && contents?.type === 'share';

  const {
    onAcceptPost,
    onErrorPost,
    onPublishPost,
    onProgramPost
  } = usePostActions();
  const { t } = useTranslation([
    'content_editor',
    'campaigns_page',
    'settings'
  ]);
  const [content, setContent] = useState<string>(originalContent);
  const [loader, setLoader] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [additionalChanges, setAdditionalChanges] = useState('');
  const { currentUser } = useSelector(selectUser);
  useEffect(() => {
    const contentArray = [
      originalContent,
      originalContent2,
      originalContent3
    ].filter(Boolean);
    setContent(() => contentArray[contentTab] as string);
    setAdditionalChanges('');
  }, [contentTab, originalContent, originalContent2, originalContent3]);

  const handleInstagramToggle = (option: 'shareInFeed' | 'shareInStories') => {
    if (
      instagramOptions[option] &&
      Object.values(instagramOptions).filter(Boolean).length === 1
    ) {
      return;
    }

    setInstagramOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const processContent = (content: string | undefined) => {
    if (!content) return '';

    return content.replace(
      /@\[[^\]]+\]\(urn:li:organization:[0-9]+\)/g,
      (dataCampaign: string) => {
        const displayName = dataCampaign.match(/@\[(.*?)\]/)?.[1] || '';
        return `@[${displayName}]`;
      }
    );
  };

  const showPostMessageBack = () => {
    setShowButton('');
  };

  const no_characters = /^(?!.*[!@#$%^&*()\-_+={}[\]|\\;:'"ª|!· º\\,<.>/?]{5}).+/;

  const regextValidationTwitter = (content: string) => {
    const regex = new RegExp(no_characters);
    return regex.test(content);
  };

  const onPublish = async () => {
    const contentSNLimit = maxCharacters || 0;
    const currentText = additionalChanges || content;

    if (isTwitter) {
      const validationContent = regextValidationTwitter(currentText);
      const contentTwitterLength = TwitterText.getTweetLength(currentText);

      if (!validationContent) {
        setBackendError(t('campaigns_page:characters'));
        return;
      } else if (contentTwitterLength > maxCharacters!) {
        setBackendError(t('campaigns_page:error_limit'));
        return;
      }
    } else {
      const contentLength = currentText.length;
      if (contentLength > contentSNLimit) {
        setBackendError(t('campaigns_page:error_limit'));
        return;
      }
    }

    let newContent = additionalChanges;
    const dataMentions =
      provider === 'linkedin'
        ? campaign?.contentPerSocialNetwork[provider].mentions
        : '';

    if (additionalChanges && dataMentions) {
      for (const mention of dataMentions) {
        const regex = /@\[[^\]]+\]\(urn:li:organization:\d+\)/;
        const matches = regex.exec(mention);

        if (matches) {
          const originalMention = matches[0];
          const regexMencion = /@\[([^\]]+)\]/;
          const mentionMatch = regexMencion.exec(originalMention);

          if (mentionMatch) {
            const mentionClear = mentionMatch[1].replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const regexMention = new RegExp(`@\\[${mentionClear}\\]`, 'g');
            const contentReplace = newContent.replace(
              regexMention,
              originalMention
            );
            newContent = contentReplace;
          }
        }
      }
    }

    const refactorContent = additionalChanges ? newContent : content;

    setLoader(true);
    setPublicationLoader(true);

    const { data, errors } = await AcceptPostRequest(
      Number(campaignId),
      refactorContent,
      provider,
      currentUser?.token,
      '',
      programmerPost,
      valueDate,
      instagramOptions.shareInFeed,
      instagramOptions.shareInStories
    );

    if (data) {
      switch (data.data) {
        case 'published':
          onPublishPost(postId, data.data);
          break;
        case 'programmed':
          onProgramPost(postId, data.data);
          break;
        case 'accepted':
          onAcceptPost(postId, data.data);
          break;
        default:
          onErrorPost(t('settings:error_admin'));
      }
      setLoader(false);
      setPublicationLoader(false);
      setShowButton(data.data);
      getRankingData();
    }

    if (errors) {
      onErrorPost(t('settings:error_admin'));
      if (errors && errors.data) {
        onErrorPost(errors.data);
      }
      setLoader(false);
      setPublicationLoader(false);
      setShowButton('pending');
    }
  };

  return (
    <ContentEditorWrapper>
      {!isManualShareCampaign && (
        <>
          <TextPlain>
            <p>{label}</p>
          </TextPlain>

          {/* Content Tabs */}
          {[originalContent, originalContent2, originalContent3].filter(Boolean)
            .length > 1 ? (
            <Tabs
              orientation="horizontal"
              tabsTitle={[originalContent, originalContent2, originalContent3]
                .filter(Boolean)
                .map((_, index) => ({
                  id: index + 1,
                  label: `${t('campaigns_page:option')} ${index + 1}`
                }))}
              tab={contentTab}
              setTab={tab => setContentTab(tab)}
              tabsContent={[originalContent, originalContent2, originalContent3]
                .filter(Boolean)
                .map((content, index) => ({
                  id: index + 1,
                  children: (
                    <TextPlain>
                      <EditorInput
                        isTwitter={isTwitter}
                        className="content-editor"
                        placeholder=""
                        content={processContent(content || '')}
                        maxCharacters={maxCharacters}
                        onChange={setAdditionalChanges}
                        disabled={disabled}
                      />
                    </TextPlain>
                  )
                }))}
            />
          ) : (
            <TextPlain>
              <EditorInput
                isTwitter={isTwitter}
                className="content-editor"
                placeholder=""
                content={processContent(originalContent || '')}
                maxCharacters={maxCharacters}
                onChange={setAdditionalChanges}
                disabled={disabled}
              />
            </TextPlain>
          )}
        </>
      )}

      {campaign && programmerPost && (
        <DateTimePicker
          locale={'es'}
          className="Calendar-time"
          onChange={setValueDate}
          value={valueDate}
          minDate={new Date()}
          maxDate={new Date(campaign?.finishDate)}
          isClockOpen={false}
          disableClock={true}
        />
      )}

      {!isManualShareCampaign && shouldValidateContent && (
        <TextPlain>
          <ContentValidator
            content={content}
            originalContent={originalContent}
          />
        </TextPlain>
      )}

      {backendError && <ErrorSpan>{backendError}</ErrorSpan>}

      {/* Toggle switches para Instagram */}
      {provider === 'instagram' && !isManualShareCampaign && (
        <div className="instagram-toggle-group">
          <div className="instagram-toggle-item">
            <span className="instagram-toggle-label">
              {t('content_editor:Perfil')}
            </span>
            <label className="instagram-toggle-switch">
              <input
                type="checkbox"
                checked={instagramOptions.shareInFeed}
                onChange={() => handleInstagramToggle('shareInFeed')}
              />
              <span className="instagram-toggle-slider"></span>
            </label>
          </div>

          <div className="instagram-toggle-item">
            <span className="instagram-toggle-label">
              {t('content_editor:Stories')}
            </span>
            <label className="instagram-toggle-switch">
              <input
                type="checkbox"
                checked={instagramOptions.shareInStories}
                onChange={() => handleInstagramToggle('shareInStories')}
              />
              <span className="instagram-toggle-slider"></span>
            </label>
          </div>
        </div>
      )}

      {programmerPost ? (
        <TextPlain>
          <Button
            className="programmerButton"
            value={t('content_editor:schedule_publication')}
            variant="tertiary"
            onClick={onPublish}
            loader={loader}
          />
          <div className="rejectParticipation">
            <span onClick={showPostMessageBack}>
              {t('content_editor:reject_participation')}
            </span>
          </div>
        </TextPlain>
      ) : (
        <TextPlain>
          <Button
            value={
              isManualShareCampaign
                ? t('content_editor:manual_share_done')
                : t('content_editor:publish')
            }
            variant="tertiary"
            onClick={onPublish}
            loader={loader}
          />
          <div className="rejectParticipation">
            <span onClick={showPostMessageBack}>
              {t('content_editor:reject_participation')}
            </span>
          </div>
        </TextPlain>
      )}
    </ContentEditorWrapper>
  );
};

export default ContentEditor;
